/* scroll/SmoothScroll.js */

/**
 * SmoothScroll — Exponential wheel-lerp scroll
 * ---------------------------------------------
 * Intercepts wheel events and lerps the real scroll position
 * toward the target using the same exponential smoothing as FXVideoScroll.
 *
 * Because window.scrollTo() is used (not transform), all existing
 * scroll-dependent code keeps working: position:sticky, getBoundingClientRect,
 * IntersectionObserver, FXImageSequence, FXVideoScroll.
 *
 * Desktop only — touch/pointer:coarse devices retain native momentum.
 * Disabled when prefers-reduced-motion is set.
 *
 * Usage:
 *   new SmoothScroll();
 *   new SmoothScroll({ tau: 0.08 }); // snappier
 */

const DEFAULTS = {
    tau:      0.04, // smoothing time constant (seconds) — 95% in ~tau*3
    minDelta: 0.5,   // px — stop threshold
};

export class SmoothScroll {

    constructor(options = {}) {

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        if (window.matchMedia('(hover: none)').matches) return;

        this.tau      = options.tau      ?? DEFAULTS.tau;
        this.minDelta = options.minDelta ?? DEFAULTS.minDelta;

        this.targetY   = window.scrollY;
        this.currentY  = window.scrollY;
        this.isRunning = false;
        this.lastTime  = 0;

        this._onWheel  = this._onWheel.bind(this);
        this._onScroll = this._onScroll.bind(this);
        this._onResize = this._onResize.bind(this);
        this._tick     = this._tick.bind(this);

        window.addEventListener('wheel',  this._onWheel,  { passive: false });
        window.addEventListener('scroll', this._onScroll, { passive: true });
        window.addEventListener('resize', this._onResize, { passive: true });

    }

    _maxScroll() {
        return Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    }

    _normalizeWheel(e) {
        let delta = e.deltaY;
        if (e.deltaMode === 1) delta *= 16;               // lines → px
        if (e.deltaMode === 2) delta *= window.innerHeight; // pages → px
        return delta;
    }

    _onWheel(e) {
        if (document.body.classList.contains('menu-active')) return;
        if (document.querySelector('#debug_window:not([hidden])')) return;
        if (e.target.closest('#cookies_manager')) return;
        e.preventDefault();
        this.targetY = Math.max(0, Math.min(this.targetY + this._normalizeWheel(e), this._maxScroll()));
        this._start();
    }

    _onScroll() {
        // Resync when scroll position changes via scrollbar, keyboard, anchor, etc.
        // Guard: during a wheel animation, scroll fires from our own scrollTo — don't override.
        if (!this.isRunning) {
            this.targetY  = window.scrollY;
            this.currentY = window.scrollY;
        }
    }

    _onResize() {
        const max    = this._maxScroll();
        this.targetY  = Math.min(this.targetY,  max);
        this.currentY = Math.min(this.currentY, max);
    }

    _start() {
        if (this.isRunning) return;
        this.isRunning = true;
        this.lastTime  = performance.now();
        document.documentElement.style.scrollBehavior = 'auto';
        requestAnimationFrame(this._tick);
    }

    _tick(now) {

        const dt    = Math.max((now - this.lastTime) / 1000, 0.001);
        this.lastTime = now;

        const diff  = this.targetY - this.currentY;
        const alpha = 1 - Math.exp(-dt / this.tau);

        if (Math.abs(diff) > this.minDelta) {
            this.currentY += diff * alpha;
            window.scrollTo({ top: this.currentY, behavior: 'instant' });
            requestAnimationFrame(this._tick);
        } else {
            this.currentY = this.targetY;
            window.scrollTo({ top: this.currentY, behavior: 'instant' });
            this.isRunning = false;
            document.documentElement.style.scrollBehavior = '';
        }

    }

    destroy() {
        window.removeEventListener('wheel',  this._onWheel);
        window.removeEventListener('scroll', this._onScroll);
        window.removeEventListener('resize', this._onResize);
        this.isRunning = false;
    }

}
